import axios from "axios";

export const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API = `${BACKEND_URL}/api`;

export const api = axios.create({
  baseURL: API,
  withCredentials: true,
});

// -- token refresh handling ------------------------------------------------
let refreshPromise = null;

async function tryRefresh() {
  if (!refreshPromise) {
    refreshPromise = axios
      .post(`${API}/auth/refresh`, {}, { withCredentials: true })
      .finally(() => {
        refreshPromise = null;
      });
  }
  return refreshPromise;
}

api.interceptors.response.use(
  (r) => r,
  async (error) => {
    const original = error.config || {};
    const status = error.response?.status;
    const url = original.url || "";

    // Never try to refresh for auth endpoints themselves
    const isAuthPath =
      url.includes("/auth/login") ||
      url.includes("/auth/register") ||
      url.includes("/auth/refresh") ||
      url.includes("/auth/session") ||
      url.includes("/auth/logout");

    // Transient gateway / network errors — retry twice with backoff before
    // surfacing to the user. Common during backend hot-reload or when the
    // preview environment briefly hibernates. Only reads are retried: a POST
    // may already have happened (e.g. a call placed) when the 502 comes back,
    // and repeating it would do it twice.
    const isTransient =
      status === 502 ||
      status === 503 ||
      status === 504 ||
      (error.code === "ERR_NETWORK" && !error.response);
    const isRead = ["get", "head", "options"].includes((original.method || "get").toLowerCase());
    if (isTransient && isRead && !isAuthPath) {
      original._transientRetries = (original._transientRetries || 0) + 1;
      if (original._transientRetries <= 2) {
        const wait = original._transientRetries === 1 ? 1500 : 3500;
        await new Promise((res) => setTimeout(res, wait));
        return api(original);
      }
    }

    if (status === 401 && !isAuthPath) {
      if (!original._retry) {
        original._retry = true;
        try {
          await tryRefresh();
          return api(original);
        } catch (_) {
          /* fall through to redirect */
        }
      }
      // Refresh failed or already retried — session is truly dead.
      // Redirect to login and swallow the error so callers don't see
      // an unhandled rejection / CRA runtime-error overlay.
      const path = window.location.pathname;
      // Guest-safe paths — the storefront and public landing/login pages
      // must NOT bounce to the owner /login when a probe hits 401.
      const isPublic =
        path === "/login" ||
        path === "/" ||
        path === "/register" ||
        path.startsWith("/shop");
      if (!isPublic) {
        window.location.replace("/login");
        // Owner /login redirect — swallow so no CRA overlay flashes.
        return new Promise(() => {});
      }
      // Public / storefront path — surface the 401 to the caller so their
      // own `.catch()` handler can render a guest-appropriate fallback
      // (e.g. hide the favourites strip). NEVER hang the promise here,
      // otherwise `Promise.all` will block the whole page forever.
      return Promise.reject(error);
    }

    // 403 role-mismatch recovery: the cookie belongs to a different role
    // than what the frontend cached (e.g. user logged in as employee in
    // another tab). Re-verify with the server and, if role changed, send
    // them to the correct home. This prevents a confusing "Forbidden"
    // toast when the real issue is a stale AuthContext.
    if (status === 403 && !isAuthPath && !original._roleRetry) {
      original._roleRetry = true;
      try {
        const { data } = await axios.get(`${API}/auth/session`, {
          withCredentials: true,
        });
        const path = window.location.pathname;
        if (!data?.user) {
          if (path !== "/login") window.location.replace("/login");
          return new Promise(() => {});
        }
        // Session is valid but role doesn't match the page we're on.
        const wantsOwner = path.startsWith("/dashboard");
        const wantsEmp = path.startsWith("/app");
        if (wantsOwner && data.user.role !== "owner") {
          window.location.replace("/app");
          return new Promise(() => {});
        }
        if (wantsEmp && data.user.role !== "employee") {
          window.location.replace("/dashboard");
          return new Promise(() => {});
        }
      } catch (_) {
        /* ignore, let original 403 bubble */
      }
    }

    // Normalize error.response.data.detail so the (many) existing
    // `toast.error(e?.response?.data?.detail || "...")` call sites never try
    // to render an object as a React child. FastAPI/Pydantic 422 responses
    // return `detail` as an array of error objects — that crashes sonner
    // with "Objects are not valid as a React child".
    if (error.response && error.response.data) {
      const d = error.response.data.detail;
      if (d && typeof d !== "string") {
        try {
          error.response.data.detail = formatApiError(error);
        } catch (_) {
          error.response.data.detail = "Please check the form and try again.";
        }
      }
    }

    return Promise.reject(error);
  }
);

export function formatApiError(err) {
  const detail = err?.response?.data?.detail;
  if (!detail) return err?.message || "Something went wrong.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail))
    return detail
      .map((e) => {
        if (!e || typeof e !== "object") return String(e);
        const field = Array.isArray(e.loc) ? e.loc.filter((p) => p !== "body").join(".") : "";
        const msg = typeof e.msg === "string" ? e.msg : JSON.stringify(e);
        return field ? `${field}: ${msg}` : msg;
      })
      .join("; ");
  if (detail && typeof detail.msg === "string") return detail.msg;
  return String(detail);
}

// The task list sends photos as links ("tasks/<id>/photo?..."), not as the
// images themselves, so the list stays small. Make them full addresses.
const photoUrl = (p) => (typeof p === "string" && p.startsWith("tasks/") ? `${API}/${p}` : p);

export function withPhotoLinks(task) {
  const t = { ...task };
  if (Array.isArray(t.proof_photos)) t.proof_photos = t.proof_photos.map(photoUrl);
  if (Array.isArray(t.ticket_attachments)) t.ticket_attachments = t.ticket_attachments.map(photoUrl);
  if (Array.isArray(t.ticket_complaints)) {
    t.ticket_complaints = t.ticket_complaints.map((c) =>
      Array.isArray(c.attachments) ? { ...c, attachments: c.attachments.map(photoUrl) } : c
    );
  }
  return t;
}
